import { useState, useCallback } from "react";
import dayjs, { Dayjs } from "dayjs";
import usePageState from "./usePageState";
import { Row } from "antd";
import { DATE_RANGE_OPTION_ENUM, DATE_RANGE_OPTIONS } from "../../applications/constants/AppConst";
import AppInputDate from "../components/AppInputDate";
import AppInputDropdown from "../components/AppInputDropdown";
import Column from "../components/Column";

export type DateRangeValue = [Dayjs, Dayjs];

interface DateRangeSelectProps {
  initSelectOptionDate?: DATE_RANGE_OPTION_ENUM;
}

export function useDateRangeSelector(props: DateRangeSelectProps) {
  const { initSelectOptionDate = DATE_RANGE_OPTION_ENUM.TO_DAY } = props;
  const today = dayjs();
  const { showError } = usePageState();

  function getRangeByOption(option: DATE_RANGE_OPTION_ENUM) {
    const today = dayjs();

    if (option === DATE_RANGE_OPTION_ENUM.CUSTOM) {
      return {
        start: today.startOf("day").valueOf(),
        end: today.endOf("day").valueOf(),
      };
    }

    if (option === DATE_RANGE_OPTION_ENUM.PREV_DAY) {
      const day = today.subtract(1, "day");
      return {
        start: day.startOf("day").valueOf(),
        end: day.endOf("day").valueOf(),
      };
    }

    const start = today
      .subtract(option - 1, "day")
      .startOf("day")
      .valueOf();

    const end = today.endOf("day").valueOf();

    return { start, end };
  }

  const initialRange = getRangeByOption(initSelectOptionDate);
  const [selectedOption, setSelectedOption] = useState<DATE_RANGE_OPTION_ENUM>(
    initSelectOptionDate ?? DATE_RANGE_OPTION_ENUM.TO_DAY,
  );
  const [startDate, setStartDate] = useState<number | undefined>(initialRange.start);
  const [endDate, setEndDate] = useState<number | undefined>(initialRange.end);

  const handleOptionChange = (value: DATE_RANGE_OPTION_ENUM) => {
    setSelectedOption(value);

    const range = getRangeByOption(value);

    setStartDate(range.start);
    setEndDate(range.end);
  };

  const validateDateRange = (): boolean => {
    if (startDate && endDate && startDate > endDate) {
      showError?.("Ngày bắt đầu không được lớn hơn ngày kết thúc.");
      return false;
    }
    return true;
  };

  const DateRangeSelectorComponent = useCallback(() => {
    return (
      <Row className="gap-2">
        <Column>
          <AppInputDropdown
            orientation="col"
            label="Chọn thời gian"
            options={DATE_RANGE_OPTIONS}
            currentValue={selectedOption}
            onChangeValue={handleOptionChange}
          />
        </Column>
        {selectedOption === DATE_RANGE_OPTION_ENUM.CUSTOM && (
          <Row className="gap-2" style={{ flexWrap: "nowrap" }}>
            <AppInputDate
              label="Ngày bắt đầu"
              currentValue={startDate}
              isShowTime={false}
              onChangeValue={(e) => setStartDate(dayjs(e).startOf("day").valueOf())}
              orientation={"col"}
            />
            <AppInputDate
              label="Ngày kết thúc"
              currentValue={endDate}
              isShowTime={false}
              onChangeValue={(e) => setEndDate(dayjs(e).endOf("day").valueOf())}
              orientation={"col"}
            />
          </Row>
        )}
      </Row>
    );
  }, [selectedOption, startDate, endDate]);

  return {
    startDate: startDate,
    endDate: endDate,
    DateRangeSelectorComponent,
    validateDateRange,
  };
}
